import { Box, Card } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "components/SoftComponents/SoftBox";
import { useEffect } from "react";
import { useSoftUIController } from "hooks/context";
import { wrapper } from "utils/wrapper";
import OccupancyFeed from "components/Camera/OccupancyFeed";
import { loadingScreen } from "utils/wrapper";
import { useMatch, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuth0 } from "@auth0/auth0-react";
import bksAPI from "services/api/bks";
import { disableMap } from "utils/map";
import { processDeviceData } from "utils/device";
import SoftTypography from "components/SoftComponents/SoftTypography";
import NodeReporting from "components/Reporting/NodeReporting";

function ReportingView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    const navigate = useNavigate();
    const match_data = useMatch('kerbsides/:group_id');
    const group_id = match_data?.params?.group_id;

    // 1. Handle redirection safely inside useEffect
    useEffect(() => {
        if (!match_data?.params) {
            navigate('/', { replace: true });
        }
    }, [match_data, navigate]);

    // Side effect for loading token (Consider moving this to a central provider if possible)
    bksAPI.loadToken(useAuth0());

    // 2. Determine UUID based on the extracted group_id
    let uuid = null;
    if (group_id) {
        uuid = group_id === 'charlotte'
            ? '3d001f-31335102-30313433'
            : '34001b-3133510a-30313433';
    }

    const device = useQuery({
        queryKey: ['getDevice', uuid, selectedOrg],
        enabled: !!uuid,
        queryFn: async () => {
            return await bksAPI.getDevice(uuid);
        },
        refetchInterval: 30 * 1000
    });

    const isGroupLoading = device.data?.organisation_id === "clearways";

    // 3. Early returns are fine here because ALL hooks have already been executed
    if (!uuid) {
        return null; // Prevents flashing content while the useEffect redirect happens
    }

    if (device.isLoading || isGroupLoading) {
        return loadingScreen(controller, true);
    }

    if (device.error) {
        return wrapper("An error has occurred fetching device or group data", controller);
    }

    // 4. Data processing
    var device_data = processDeviceData(device.dataUpdatedAt, device.data);

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? "calc(100svh - 3.8rem - 16px)" : "calc(100svh - 5.5rem - 16px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox height='100%'>
                <SoftBox m={1}>
                    <Card sx={{ overflow: "visible", width: '100%', borderRadius: '16px' }}>
                        <SoftBox mx={2} mt={1} mb={1}>
                            <SoftBox display='flex' flexDirection='row' justifyContent='space-between'>
                                <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="center">
                                    <SoftTypography variant="h5" fontWeight="bold">Mercury Reporting</SoftTypography>
                                    <SoftTypography variant="body3">
                                        For {group_id ? group_id.charAt(0).toUpperCase() + group_id.slice(1) + ' St' : ''}
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    </Card>
                </SoftBox>
                <SoftBox sx={{ overflowY: 'auto', height: `calc(100% - 90px)` }}>
                    <Box display="flex" flex={1} flexDirection={{ xxs: "column", lg: "row" }} px={1} sx={{ height: '100%' }}>
                        <Box flex={{ xxs: 12, lg: 7 }} pb={{ xxs: 1, lg: 0 }} pr={{ xxs: 0, lg: 0.5 }}>
                            <Box sx={{ height: deviceLayout != "mobile" ? "calc(100svh - 11.2rem - 16px)" : "100%" }}>
                                <OccupancyFeed device={device_data} user={user} deviceLayout={deviceLayout} sx={{ height: "100%" }} />
                            </Box>
                        </Box>
                        <Box flex={{ xxs: 12, lg: 5 }} pl={{ xxs: 0, lg: 0.5 }} sx={{ minWidth: 0 }}>
                            <NodeReporting device={device_data} user={user} deviceLayout={deviceLayout} />
                        </Box>
                    </Box>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    ), controller, true);
}

ReportingView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
};

export default ReportingView;